// definitions
import { StripeOrgAccountXeroOrg, AccountStripeOrg, StripeProduct, StripeTaxRate, XeroAPIContact } from '../../../../../back-end/utilities/apiDefinitions'
import { StripeOrgAccountXeroOrgMapping } from './settings'

const validateMappings = (
	stripeOrgs: (AccountStripeOrg & { products: StripeProduct[]; taxRates: StripeTaxRate[] })[],
	orgMappings: (StripeOrgAccountXeroOrg & { edited?: boolean })[]
) => {
	// not every stripe account must be mapped to a xero account, but if it is, all products must be mapped to xero account/item as per mapping type
	return stripeOrgs
		.filter((stripeOrg) => orgMappings.some((orgMapping) => orgMapping.stripeOrgID === stripeOrg.stripeOrg?.stripeOrgID))
		.every((stripeOrg) => {
			const orgMapping = orgMappings.find((orgMapping) => orgMapping.stripeOrgID === stripeOrg.stripeOrg?.stripeOrgID)!
			return (
				!orgMapping.accountXeroOrgID ||
				(stripeOrg.products.every((product) =>
					orgMapping.stripeProductXeroMappings!.some(
						(productMapping) =>
							productMapping.stripeProductID === product.stripeProductID &&
							productMapping[`xero${orgMapping.stripeOrgAccountXeroOrgMappingType!}ID`]
					)
				) &&
					stripeOrg.taxRates.every((taxRate) =>
						orgMapping.stripeTaxRateXeroTaxRates!.some((taxRateMapping) => taxRateMapping.stripeTaxRateID === taxRate.stripeTaxRateID)
					) &&
					(!orgMapping.stripeOrgAccountXeroOrgSettings?.stripeFeesBillsEnabled ||
						((orgMapping.stripeOrgAccountXeroOrgSettings.stripeFeesXeroContactID ||
							orgMapping.stripeOrgAccountXeroOrgSettings.stripeFeesXeroContactXeroID) &&
							orgMapping.stripeOrgAccountXeroOrgSettings.stripeFeesXeroAccountID &&
							orgMapping.stripeOrgAccountXeroOrgSettings.stripeFeesXeroTaxRateID)))
			)
		})
}

const handleOrgAdd = (
	orgMappings: StripeOrgAccountXeroOrgMapping[],
	updateOrgMappings: React.Dispatch<React.SetStateAction<StripeOrgAccountXeroOrgMapping[]>>,
	stripeOrgID: string,
	accountXeroOrgID: string
) => {
	const updatedOrgMappings = orgMappings.concat([
		{
			stripeOrgAccountXeroOrgMappingType: 'Account',
			stripeOrgID: stripeOrgID,
			accountXeroOrgID: accountXeroOrgID,
			stripeProductXeroMappings: [],
			stripeTaxRateXeroTaxRates: [],
			edited: true,
		},
	])
	updateOrgMappings(updatedOrgMappings)
}

const handleOrgUpdate = (
	orgMappings: StripeOrgAccountXeroOrgMapping[],
	updateOrgMappings: React.Dispatch<React.SetStateAction<StripeOrgAccountXeroOrgMapping[]>>,
	stripeOrgID: string,
	name: string,
	value: string
) => {
	const updatedOrgMappings = orgMappings
		.map((mapping) =>
			mapping.stripeOrgID === stripeOrgID
				? {
						...mapping,
						[name]: value,
						edited: true,
				  }
				: mapping
		)
		.filter((mapping) => mapping.stripeOrgAccountXeroOrgID || mapping.accountXeroOrgID)
	updateOrgMappings(updatedOrgMappings)
}

const handleProductAdd = (
	orgMappings: StripeOrgAccountXeroOrgMapping[],
	updateOrgMappings: React.Dispatch<React.SetStateAction<StripeOrgAccountXeroOrgMapping[]>>,
	stripeOrgID: string,
	stripeProductID: string,
	name: string,
	value: string
) => {
	const updatedOrgMappings = orgMappings.map((mapping) =>
		mapping.stripeOrgID === stripeOrgID
			? {
					...mapping,
					stripeProductXeroMappings: mapping.stripeProductXeroMappings!.concat({
						stripeProductID: stripeProductID,
						[name]: value,
					}),
					edited: true,
			  }
			: mapping
	)
	updateOrgMappings(updatedOrgMappings)
}

const handleProductUpdate = (
	orgMappings: StripeOrgAccountXeroOrgMapping[],
	updateOrgMappings: React.Dispatch<React.SetStateAction<StripeOrgAccountXeroOrgMapping[]>>,
	stripeOrgID: string,
	stripeProductID: string,
	name: string,
	value: string
) => {
	const updatedOrgMappings = orgMappings.map((mapping) =>
		mapping.stripeOrgID === stripeOrgID
			? {
					...mapping,
					stripeProductXeroMappings: mapping.stripeProductXeroMappings!.map((productMapping) =>
						productMapping.stripeProductID === stripeProductID
							? {
									...productMapping,
									...(value && name === 'xeroAccountID'
										? {
												xeroAccountID: value,
												xeroItemID: null,
										  }
										: value && name === 'xeroItemID'
										? {
												xeroItemID: value,
												xeroAccountID: null,
										  }
										: {
												xeroAccountID: null,
												xeroItemID: null,
										  }),
							  }
							: productMapping
					),
					edited: true,
					error: false,
			  }
			: mapping
	)
	updateOrgMappings(updatedOrgMappings)
}

const handleApplyToAllProducts = (
	orgMappings: StripeOrgAccountXeroOrgMapping[],
	updateOrgMappings: React.Dispatch<React.SetStateAction<StripeOrgAccountXeroOrgMapping[]>>,
	stripeProducts: StripeProduct[],
	stripeOrgID: string,
	name: string,
	value: string
) => {
	const updatedOrgMappings = orgMappings.map((mapping) =>
		mapping.stripeOrgID === stripeOrgID
			? {
					...mapping,
					stripeProductXeroMappings: stripeProducts.map((product) => {
						const existingMapping = mapping.stripeProductXeroMappings?.find((mapping) => mapping.stripeProductID === product.stripeProductID)
						return existingMapping
							? {
									...existingMapping,
									[name]: value,
							  }
							: {
									stripeProductID: product.stripeProductID,
									[name]: value,
							  }
					}),
					edited: true,
			  }
			: mapping
	)
	updateOrgMappings(updatedOrgMappings)
}

const handleTaxRateAdd = (
	orgMappings: StripeOrgAccountXeroOrgMapping[],
	updateOrgMappings: React.Dispatch<React.SetStateAction<StripeOrgAccountXeroOrgMapping[]>>,
	stripeOrgID: string,
	stripeTaxRateID: string,
	name: string,
	value: string
) => {
	const updatedOrgMappings = orgMappings.map((mapping) =>
		mapping.stripeOrgID === stripeOrgID
			? {
					...mapping,
					stripeTaxRateXeroTaxRates: mapping.stripeTaxRateXeroTaxRates!.concat({
						stripeTaxRateID: stripeTaxRateID,
						[name]: value,
					}),
					edited: true,
			  }
			: mapping
	)
	updateOrgMappings(updatedOrgMappings)
}

const handleTaxRateUpdate = (
	orgMappings: StripeOrgAccountXeroOrgMapping[],
	updateOrgMappings: React.Dispatch<React.SetStateAction<StripeOrgAccountXeroOrgMapping[]>>,
	stripeOrgID: string,
	stripeTaxRateID: string,
	name: string,
	value: string
) => {
	const updatedOrgMappings = orgMappings.map((mapping) =>
		mapping.stripeOrgID === stripeOrgID
			? {
					...mapping,
					stripeTaxRateXeroTaxRates: mapping.stripeTaxRateXeroTaxRates!.map((taxRateMapping) =>
						taxRateMapping.stripeTaxRateID === stripeTaxRateID
							? {
									...taxRateMapping,
									xeroTaxRateID: value,
							  }
							: taxRateMapping
					),
					edited: true,
					error: false,
			  }
			: mapping
	)
	updateOrgMappings(updatedOrgMappings)
}

const handleSettingsUpdate = (
	orgMappings: StripeOrgAccountXeroOrgMapping[],
	updateOrgMappings: React.Dispatch<React.SetStateAction<StripeOrgAccountXeroOrgMapping[]>>,
	stripeOrgID: string,
	values: { [name: string]: string | boolean | XeroAPIContact | undefined }
) => {
	const updatedOrgMappings = orgMappings.map((mapping) =>
		mapping.stripeOrgID === stripeOrgID
			? {
					...mapping,
					stripeOrgAccountXeroOrgSettings: {
						...mapping.stripeOrgAccountXeroOrgSettings,
						...values,
					},
					edited: true,
					error: false,
			  }
			: mapping
	)
	updateOrgMappings(updatedOrgMappings)
}

export {
	validateMappings,
	handleOrgAdd,
	handleOrgUpdate,
	handleProductAdd,
	handleProductUpdate,
	handleApplyToAllProducts,
	handleTaxRateAdd,
	handleTaxRateUpdate,
	handleSettingsUpdate,
}
